/* eslint-disable max-len */
/**
 * TdDate — date input that matches TdInput's visual treatment.
 *
 * Native `<input type="date">` works on desktop but the calendar widget
 * behaves inconsistently inside fixed/transformed containers (Dialog) and
 * across browsers. This component wraps `react-datepicker` to render a
 * portal-based calendar that anchors properly anywhere — and styles the
 * trigger to match the rest of our inputs.
 *
 * Controlled: parent passes ISO yyyy-mm-dd string (or empty) and onChange
 * receives the same shape. Use a Controller in react-hook-form forms.
 */
import React, { useRef } from 'react';
import DatePickerLib from 'react-datepicker';
import moment from 'moment';
import { Capacitor } from '@capacitor/core';
import { CalendarIcon } from '@heroicons/react/24/outline';
import 'react-datepicker/dist/react-datepicker.css';
import ValidationIndicator from '../tdinput/ValidationIndicator';

// react-datepicker@4 ships old class-component types that don't satisfy
// React 18's ElementClass shape. Cast away — runtime is unaffected.
const DatePicker = DatePickerLib as unknown as React.FC<any>;

type Props = {
  id: string;
  name: string;
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  errorMessage?: string;
  isValid?: boolean;
  width?: string;
  /** Reserve the error line's height when there is no error (no layout shift on blur validation). */
  reserveErrorSpace?: boolean;
  /** Set true to allow past dates (default false — for renewal/expiry use cases). */
  allowPast?: boolean;
};

export const TdDate: React.FC<Props> = ({
  id, name, label, value, onChange, placeholder = 'dd/mm/yyyy',
  required = false, error = false, errorMessage, isValid = false, width,
  allowPast = true, reserveErrorSpace = false,
}) => {
  const showInvalid = !!error;
  const showValid = !showInvalid && isValid;
  const stateClass = showInvalid
    ? 'border-2 border-doqit-highlight-pink text-doqit-highlight-pink placeholder:text-doqit-highlight-pink'
    : 'border border-doqit-purple-150 text-doqit-deep-purple placeholder:text-doqit-mid-purple';
  const widthClass = width || 'w-full';
  const baseInputClass = 'h-[71px] rounded-[12px] font-body font-medium text-[19px] leading-[19px] tracking-[0.5px] bg-doqit-purple-25 outline-none placeholder:opacity-70';
  const insetShadow = 'shadow-[inset_2px_2px_4px_rgba(57,38,79,0.2)]';
  const inputClass = `block ${widthClass} pl-12 pr-[21px] ${baseInputClass} ${insetShadow} ${stateClass}`;

  const selectedDate = value ? moment(value, ['YYYY-MM-DD', 'DD/MM/YYYY']).toDate() : null;

  // Container ref so the calendar popper anchors inside the parent flow.
  const wrapperRef = useRef<HTMLDivElement>(null);

  // On Capacitor native (iOS/Android), use the OS native date picker via
  // <input type="date">. react-datepicker's underlying text input causes
  // the on-screen keyboard to pop up over the calendar UI on focus —
  // ugly and the calendar gets covered. Native input triggers the OS
  // date wheel/calendar instead, with no keyboard.
  const isNative = Capacitor.isNativePlatform();

  // ISO yyyy-mm-dd is the canonical value we store; native <input type="date">
  // already expects/produces this format, so it maps 1:1.
  const isoValue = value
    ? moment(value, ['YYYY-MM-DD', 'DD/MM/YYYY']).format('YYYY-MM-DD')
    : '';

  // Pretty display of the selected date — used by the native overlay so we
  // don't depend on iOS Safari's quirky <input type="date"> value rendering
  // (which on iOS ignores our padding + line-height and renders the date
  // text at the wrong offset, overlapping the next row).
  const displayValue = value
    ? moment(value, ['YYYY-MM-DD', 'DD/MM/YYYY']).format('D MMM YYYY')
    : '';

  return (
    <label htmlFor={id} className="relative block">
      {label && (
        <span
          // min-height covers a 2-line label so two side-by-side fields keep
          // their inputs aligned even when one label wraps and the other
          // doesn't (e.g. "Start date" + "End date (leave blank if current)").
          className="block text-doqit-mid-purple text-[15px] leading-[19px] tracking-[0.5px] font-medium font-body mb-1.5 min-h-[38px]"
        >
          {label}
          {required && <span className="text-doqit-highlight-pink ml-0.5">*</span>}
        </span>
      )}
      <div ref={wrapperRef} className="relative">
        <CalendarIcon className="pointer-events-none w-6 h-6 absolute left-3 top-1/2 -translate-y-1/2 text-doqit-mid-purple/70 z-10" />
        {isNative ? (
          <>
            {/* Native input: full-area for tap-to-open, but its own value
                text is hidden — iOS renders it inconsistently and at the
                wrong vertical offset. We overlay our own formatted text. */}
            <input
              id={id}
              name={name}
              type="date"
              value={isoValue}
              onChange={(e) => onChange(e.target.value || '')}
              min={allowPast ? undefined : new Date().toISOString().slice(0, 10)}
              className={inputClass}
              placeholder={placeholder}
              style={{
                color: 'transparent',
                WebkitTextFillColor: 'transparent',
                // Make sure the iOS picker still fires on tap.
                background: 'inherit',
              }}
            />
            {/* Overlay shows the formatted date (or placeholder when empty),
                positioned exactly where TdInput puts its value so date and
                text inputs line up visually in the same row. */}
            <span
              className="pointer-events-none absolute left-12 right-[21px] top-1/2 -translate-y-1/2 font-body font-medium text-[19px] leading-[19px] tracking-[0.5px]"
              style={{
                color: (() => {
                  if (displayValue) return showInvalid ? '#E516C5' : '#39264F';
                  return showInvalid ? '#E516C5B3' : '#9A89AE';
                })(),
              }}
              aria-hidden="true"
            >
              {displayValue || placeholder}
            </span>
          </>
        ) : (
          <DatePicker
            id={id}
            name={name}
            selected={selectedDate}
            onChange={(d: Date | null) => onChange(d ? moment(d).format('YYYY-MM-DD') : '')}
            dateFormat="dd/MM/yyyy"
            placeholderText={placeholder}
            minDate={allowPast ? undefined : new Date()}
            peekNextMonth
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            className={inputClass}
            popperPlacement="bottom-start"
            popperProps={{
              // Render the calendar through a portal so it can escape the
              // Dialog's stacking context and overflow-hidden scrollers.
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              strategy: 'fixed' as any,
            }}
            wrapperClassName="block w-full"
          />
        )}
        {showInvalid && (
          <ValidationIndicator state="invalid" className="absolute right-3 -top-[10px]" />
        )}
        {showValid && (
          <ValidationIndicator state="valid" className="absolute right-3 -top-[10px]" />
        )}
      </div>
      {showInvalid && errorMessage && (
        <p className={`${name}-error mt-1.5 text-doqit-highlight-pink text-[15px] leading-[19px] tracking-[0.5px] font-bold font-body`}>
          {errorMessage}
        </p>
      )}
      {/* Holds the error line's height while there is no error, so blur
          validation doesn't push everything below it down (opt-in). */}
      {reserveErrorSpace && !(showInvalid && errorMessage) && (
        <span aria-hidden="true" className="block mt-1.5 h-[19px]" />
      )}
    </label>
  );
};

export default TdDate;
